import type { PartnerHealth } from "@payezy/api-client/admin";
import { listPartnerEventsOptions, listPartnersOptions, setPartnerOverrideMutation } from "@payezy/api-client/admin/query";
import { Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, formatDate, Input, PageHeader, Select, Table, TD, Textarea, TH, THead, TR, toast } from "@payezy/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ActionDialog } from "../components/ActionDialog";
import { showError } from "../lib/notify";
import { healthTone, useStaff } from "../lib/staff";

export function PartnersPage() {
  const me = useStaff();
  const partners = useQuery({ ...listPartnersOptions(), refetchInterval: 15_000 });
  const [status, setStatus] = useState("failed");
  const [partner, setPartner] = useState("");
  const events = useQuery({ ...listPartnerEventsOptions({ query: { status: status || undefined, partner: partner || undefined, limit: 100 } }), placeholderData: keepPreviousData });
  const [overriding, setOverriding] = useState<PartnerHealth | null>(null);
  const [replay, setReplay] = useState<string | null>(null);
  return (
    <>
      <PageHeader title="Partners" description="Circuit breaker state drives routing: payouts avoid partners that are down." />
      <Card>
        <Table>
          <THead><TR><TH>Partner</TH><TH>State</TH><TH className="text-right">Calls</TH><TH className="text-right">Failures</TH><TH className="text-right">Error rate</TH><TH>Checked</TH><TH /></TR></THead>
          <tbody>
            {partners.data?.items.map((p) => (
              <TR key={p.partner}>
                <TD className="font-medium">{p.partner}</TD>
                <TD><Badge tone={healthTone[p.state]}>{p.state}</Badge>{p.override && <span className="ml-2 text-xs text-amber-700">forced {p.override}</span>}</TD>
                <TD className="text-right tabular-nums">{p.calls}</TD>
                <TD className="text-right tabular-nums">{p.failures}</TD>
                <TD className="text-right tabular-nums">{(p.error_rate * 100).toFixed(1)}%</TD>
                <TD>{formatDate(p.checked_at, true)}</TD>
                <TD className="text-right">{me.can("recon:manage") && <Button size="sm" variant="secondary" onClick={() => setOverriding(p)}>Override</Button>}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card className="mt-6">
        <CardHeader
          title="Inbound webhooks"
          description="Payloads are not shown. Failed events can be replayed; processing is idempotent."
          actions={
            <div className="flex gap-2">
              <Select className="w-40" value={partner} onChange={(e) => setPartner(e.target.value)}>
                <option value="">All partners</option>
                {partners.data?.items.map((p) => <option key={p.partner} value={p.partner}>{p.partner}</option>)}
              </Select>
              <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {["received", "processed", "ignored", "failed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          }
        />
        <Table>
          <THead><TR><TH>Received</TH><TH>Partner</TH><TH>Type</TH><TH>Status</TH><TH className="text-right">Attempts</TH><TH>Error</TH><TH /></TR></THead>
          <tbody>
            {events.data?.items.map((e) => (
              <TR key={e.id}>
                <TD>{formatDate(e.received_at, true)}</TD>
                <TD>{e.partner}</TD>
                <TD className="font-mono text-xs">{e.event_type}</TD>
                <TD><Badge tone={e.status === "failed" ? "danger" : e.status === "processed" ? "success" : "neutral"}>{e.status}</Badge></TD>
                <TD className="text-right tabular-nums">{e.attempts}</TD>
                <TD className="max-w-72 truncate text-xs text-red-700" title={e.error}>{e.error || "—"}</TD>
                <TD className="text-right">
                  {e.status === "failed" && me.can("recon:manage") && <Button size="sm" variant="secondary" onClick={() => setReplay(e.id)}>Replay</Button>}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
        {events.data?.items.length === 0 && <EmptyState title="No events" />}
      </Card>
      {overriding && <OverrideDialog p={overriding} onClose={() => setOverriding(null)} />}
      {replay && <ActionDialog type="webhook.replay" eventId={replay} open onOpenChange={(o) => !o && setReplay(null)} />}
    </>
  );
}

function OverrideDialog({ p, onClose }: { p: PartnerHealth; onClose: () => void }) {
  const qc = useQueryClient();
  const [state, setState] = useState<"up" | "down" | "">(p.override ? "" : p.state === "down" ? "up" : "down");
  const [minutes, setMinutes] = useState("60");
  const [reason, setReason] = useState("");
  const save = useMutation({
    ...setPartnerOverrideMutation(),
    onSuccess: () => {
      toast.success("Override saved.");
      onClose();
      void qc.invalidateQueries();
    },
    onError: showError,
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Override ${p.partner}`}
      description="Forcing a partner down drains new payouts to other partners; forcing it up ignores its error rate. Requires a recent sign-in."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={reason.trim().length < 10} onClick={() => save.mutate({ path: { partner: p.partner }, body: { state, minutes: Number(minutes) || 60, reason } })}>Save</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="State">
          <Select value={state} onChange={(e) => setState(e.target.value as "up" | "down" | "")}>
            <option value="down">Force down</option>
            <option value="up">Force up</option>
            <option value="">Clear override (automatic)</option>
          </Select>
        </Field>
        {state && <Field label="For (minutes)"><Input inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} /></Field>}
        <Field label="Reason"><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="INC-42: partner reports degraded ACH processing" /></Field>
      </div>
    </Dialog>
  );
}
